import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, BedDouble, Bath, Maximize2, Car, Calendar, Phone, Mail, MessageCircle, Heart, Share2, Check, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import PropertyCard from '../components/PropertyCard';
import api, { enrichProperty, enrichAgent } from '../lib/api';

export default function PropertyDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const [property, setProperty] = useState(null);
  const [agent, setAgent] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [activeVideo, setActiveVideo] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  useEffect(() => {
    setActiveImg(0); setShowVideo(false); setActiveVideo(0);
    api.get('/properties/' + id).then(async (r) => {
      const p = enrichProperty(r.data);
      setProperty(p);
      setForm((f) => ({ ...f, message: 'I am interested in ' + p.title + '. Please contact me.' }));
      if (p.agentId) {
        api.get('/agents/' + p.agentId).then(r2 => setAgent(enrichAgent(r2.data)));
      }
      api.get('/properties', { params: { type: p.type, limit: 4 } }).then(r3 => {
        const items = (r3.data?.items || []).map(enrichProperty).filter(x => x.id !== p.id).slice(0, 3);
        setSimilar(items);
      });
    }).catch(() => setProperty(null));
  }, [id]);

  if (!property) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading property...</div>;

  const videos = property.videos?.length ? property.videos : (property.videoUrl ? [property.videoUrl] : []);
  const next = () => setActiveImg((activeImg + 1) % property.gallery.length);
  const prev = () => setActiveImg((activeImg - 1 + property.gallery.length) % property.gallery.length);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/inquiries', { ...form, property_id: property.id, agent_id: property.agentId, subject: 'property' });
      toast({ title: 'Inquiry sent!', description: 'Our advisor will contact you within 24 hours.' });
      setForm({ ...form, name: '', email: '', phone: '' });
    } catch {
      toast({ title: 'Failed to send', description: 'Please try again later.', variant: 'destructive' });
    }
  };

  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-2 text-sm text-neutral-500">
        <Link to="/" className="hover:text-[#DC2626]">Home</Link> / <Link to="/properties" className="hover:text-[#DC2626]">Properties</Link> / <span className="text-neutral-700">{property.title}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="relative bg-neutral-900 rounded-xl overflow-hidden">
            <div className="relative aspect-[16/10]">
              {showVideo && videos.length ? (
                <video key={activeVideo} src={videos[activeVideo]} controls autoPlay className="w-full h-full object-cover bg-black" />
              ) : (
                <img src={property.gallery[activeImg]} alt={property.title} className="w-full h-full object-cover" />
              )}
              {!showVideo && property.gallery.length > 1 && (
                <>
                  <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"><ChevronLeft className="w-5 h-5" /></button>
                  <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"><ChevronRight className="w-5 h-5" /></button>
                </>
              )}
              <div className="absolute top-3 left-3 flex gap-2">
                <Badge className="bg-[#DC2626] text-white hover:bg-[#DC2626]">{property.transactionType}</Badge>
                <Badge variant="secondary" className="bg-white text-neutral-900">{property.type}</Badge>
              </div>
              <div className="absolute top-3 right-3 flex gap-2">
                {videos.length > 0 && !showVideo && (
                  <button onClick={() => { setActiveVideo(0); setShowVideo(true); }} className="px-3 h-9 rounded-full bg-[#DC2626] text-white text-sm font-semibold flex items-center gap-1"><Play className="w-4 h-4" /> {videos.length > 1 ? `Tours (${videos.length})` : 'Tour'}</button>
                )}
                <button className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center"><Heart className="w-4 h-4" /></button>
                <button className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center"><Share2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1 p-1 bg-neutral-900">
              {property.gallery.map((img, i) => (
                <button key={i} onClick={() => { setActiveImg(i); setShowVideo(false); }} className={'relative aspect-video overflow-hidden rounded ' + (activeImg === i && !showVideo ? 'ring-2 ring-[#DC2626]' : 'opacity-75 hover:opacity-100')}>
                  <img src={img} alt={'thumb ' + (i + 1)} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            {showVideo && videos.length > 1 && (
              <div className="flex flex-wrap gap-2 px-2 pb-2 bg-neutral-900">
                {videos.map((_, i) => (
                  <button key={i} onClick={() => setActiveVideo(i)} className={'px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ' + (activeVideo === i ? 'bg-[#DC2626] text-white' : 'bg-white/90 text-neutral-800 hover:bg-white')}>
                    <Play className="w-3 h-3" /> Video {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl mt-6 p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-neutral-900">{property.title}</h1>
                <p className="flex items-center gap-1 text-neutral-500 mt-1"><MapPin className="w-4 h-4" /> {property.address}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-[#DC2626]">{property.priceLabel}</p>
                <p className="text-xs text-neutral-500">Listing ID: {property.id?.slice(0, 8)}</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4 pt-5 border-t border-neutral-100">
              {property.bedrooms > 0 && <div><BedDouble className="w-5 h-5 text-[#DC2626] mb-1" /><p className="text-xs text-neutral-500">Bedrooms</p><p className="font-semibold">{property.bedrooms}</p></div>}
              {property.bathrooms > 0 && <div><Bath className="w-5 h-5 text-[#DC2626] mb-1" /><p className="text-xs text-neutral-500">Bathrooms</p><p className="font-semibold">{property.bathrooms}</p></div>}
              <div><Maximize2 className="w-5 h-5 text-[#DC2626] mb-1" /><p className="text-xs text-neutral-500">Floor Area</p><p className="font-semibold">{property.floorArea} sqm</p></div>
              <div><Maximize2 className="w-5 h-5 text-[#DC2626] mb-1" /><p className="text-xs text-neutral-500">Lot Area</p><p className="font-semibold">{property.lotArea} sqm</p></div>
              {property.parking > 0 && <div><Car className="w-5 h-5 text-[#DC2626] mb-1" /><p className="text-xs text-neutral-500">Parking</p><p className="font-semibold">{property.parking} car(s)</p></div>}
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl mt-6 p-6">
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Description</h2>
            <p className="text-neutral-700 leading-relaxed whitespace-pre-line">{property.description}</p>
            <div className="mt-6 grid grid-cols-2 gap-3 pt-5 border-t border-neutral-100">
              <div className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-[#DC2626]" /> Year built: <strong>{property.yearBuilt}</strong></div>
              <div className="flex items-center gap-2 text-sm">Status: <strong>{property.status}</strong></div>
            </div>
          </div>

          {property.features?.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-xl mt-6 p-6">
              <h2 className="text-xl font-bold text-neutral-900 mb-4">Features & Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {property.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-neutral-700"><Check className="w-4 h-4 text-[#DC2626]" /> {f}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-xl p-6 sticky top-24">
            {agent && (
              <>
                <div className="flex items-center gap-4">
                  <img src={agent.photo} alt={agent.name} className="w-16 h-16 rounded-full object-cover" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-neutral-500">Listing Advisor</p>
                    <Link to={'/agent/' + agent.id} className="font-bold text-neutral-900 hover:text-[#DC2626]">{agent.name}</Link>
                    <p className="text-xs text-neutral-500">{agent.title}</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <a href={'tel:' + agent.phone} className="flex items-center gap-2 text-sm text-neutral-700 hover:text-[#DC2626]"><Phone className="w-4 h-4" /> {agent.phone}</a>
                  <a href={'mailto:' + agent.email} className="flex items-center gap-2 text-sm text-neutral-700 hover:text-[#DC2626]"><Mail className="w-4 h-4" /> {agent.email}</a>
                </div>
              </>
            )}

            <form onSubmit={submit} className="mt-5 pt-5 border-t border-neutral-100 space-y-3">
              <h3 className="font-bold text-neutral-900 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#DC2626]" /> Inquire about this property</h3>
              <Input required placeholder="Your name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
              <Input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
              <Input required placeholder="Phone number" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
              <Textarea rows={4} placeholder="Message" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} />
              <Button type="submit" className="btn-primary w-full">Send Inquiry</Button>
            </form>
          </div>
        </aside>
      </div>

      {similar.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h2 className="text-2xl font-bold text-neutral-900 mb-6">Similar Properties</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {similar.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
